import React, { useState, useEffect } from "react";
import axios from "axios";
import { api } from "../utils/Api";
import {
	Button,
	Col,
	Flex,
	Form,
	Input,
	notification,
	Row,
	Typography,
	Upload,
} from "antd";
import { CustomSpinner, CustomTable } from "../utils/CustomComponents";
import {
	PlusCircleFilled,
	FileImageFilled,
	UploadOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

const optionsDate = {
	month: "numeric",
	year: "numeric",
	day: "numeric",
	hour: "numeric",
	minute: "numeric",
	second: "numeric",
	hour12: true,
};

const QCSamplingListAdd = ({ jobId }) => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(true);
	const [loadingButton, setLoadingButton] = useState(true);
	const [loadingTable, setLoadingTable] = useState(true);
	const [apiNotification, contextHolder] = notification.useNotification();
	// const [evidenceRemoved, setEvidenceRemoved] = useState(false);
	const [evidenceAdded, setEvidenceAdded] = useState(false);
	const [evidences, setEvidences] = useState([]);

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	/**
	 ** Options header (columns)
	 */
	const columns = [
		{
			title: "Date",
			dataIndex: "date",
			key: "driver",
			render: (value) => {
				return <Text strong>{value}</Text>;
			},
		},
		{
			title: "Evidence",
			dataIndex: "evidence",
			key: "evidence",
			render: (value) => {
				return (
					<Button
						type="primary"
						href={value}
						target="_blank"
						icon={<FileImageFilled twoToneColor={"white"} />}
					>
						View Image
					</Button>
				);
			},
		},
		{
			title: "Comment",
			dataIndex: "comment",
			key: "comment",
			render: (value) => {
				return <Text strong>{value}</Text>;
			},
		},
		{
			title: "User",
			dataIndex: "user",
			key: "user",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
	];

	useEffect(() => {
		getSamplings();
	}, []);

	const RenderTable = () => {
		return (
			<CustomTable
				columns={columns}
				data={evidences}
				isScroll={false}
				loading={loadingTable}
			/>
		);
	};

	const getSamplings = async () => {
		try {
			setLoadingTable(true);

			const url = api + `/job/get/qcevidence/${jobId}`;
			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };
			const { data } = await axios.get(url);

			if (data.success) {
				setEvidences([
					...data.payload.qcSampling.map((item, index) => {
						return {
							id: index,
							key: index,
							comment: item.comment,
							evidence: item.evidence,
							date: new Intl.DateTimeFormat("en-US", optionsDate).format(
								new Date(item.createdAt)
							),
							user: item.user.name,
						};
					}),
				]);
				form.resetFields();
				setLoadingTable(false);
				setLoading(false);
				setLoadingButton(false);
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			openNotification("error", "Error", err.message);
			console.error(err.message);
			return null;
		}
	};

	const createSampling = async (values) => {
		try {
			setLoadingButton(true);
			const formData = new FormData();

			if (evidenceAdded && values.evidence && values.evidence.length > 0) {
				formData.append("evidence", values.evidence[0].originFileObj);
			}

			formData.append("comment", values.comment);

			const url = api + `/job/add/qcevidence/${jobId}`;
			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };
			const config = {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			};

			const { data } = await axios.post(url, formData, config);
			if (data.success) {
				openNotification("success", "Success", data.message);
				await getSamplings();
			} else {
				openNotification("error", "Error", data.message);
				setLoadingButton(false);
			}
		} catch (err) {
			setLoadingButton(false);
			openNotification("error", "Error", "Error while trying to add evidence");
			console.error(err.message);
			return null;
		}
	};

	const onFinish = (values) => {
		createSampling(values);
	};

	const normFile = (e) => {
		if (Array.isArray(e)) {
			return e;
		}
		return e?.fileList;
	};

	const onChangeEvidence = ({ file }) => {
		if (file.status === "removed") {
			// setEvidenceRemoved(true);
			setEvidenceAdded(false);
		}
		if (!file.status) {
			setEvidenceAdded(true);
			// setEvidenceRemoved(false);
		}
	};

	const CustomForm = () => (
		<Form
			form={form}
			layout="vertical"
			onFinish={onFinish}
			style={{ marginTop: "20px" }}
		>
			<Row gutter={16}>
				<Col span={4}>
					<Form.Item
						label="Evidence"
						name="evidence"
						valuePropName="fileList"
						getValueFromEvent={normFile}
						rules={[
							{
								required: true,
								message: "Evidence is required",
							},
						]}
					>
						<Upload
							beforeUpload={() => false}
							maxCount={1}
							multiple={false}
							accept=".png, .jpg, .jpeg"
							onChange={onChangeEvidence}
						>
							<Button
								icon={<UploadOutlined />}
								size="large"
								style={{
									width: "100%",
								}}
							>
								Upload
							</Button>
						</Upload>
					</Form.Item>
				</Col>
				<Col span={20}>
					<Form.Item
						label="Comment"
						name="comment"
						rules={[
							{
								required: true,
								message: "Comment is required",
							},
						]}
					>
						<Input size="large" placeholder="Comment" />
					</Form.Item>
				</Col>
			</Row>
			<Row gutter={16}>
				<Col span={24} sm={12} md={8} lg={4}>
					<Button
						type="primary"
						htmlType="submit"
						loading={loadingButton}
						size="large"
						style={{
							width: "100%",
						}}
						icon={<PlusCircleFilled twoToneColor={"white"} />}
					>
						Add
					</Button>
				</Col>
			</Row>
		</Form>
	);

	return (
		<div>
			{contextHolder}
			<Flex vertical gap={"middle"}>
				<RenderTable />
				{loading ? <CustomSpinner /> : <CustomForm />}
			</Flex>
		</div>
	);
};

export default QCSamplingListAdd;
